import { ApiError, type ApiResponse, type SongMedia, type SyncQueueItem } from '@/types'
import { env } from '@/lib/env'
import { getAuthToken } from '@/lib/authToken'
import { nowISO, sleep, uid } from '@/lib/utils'
import { repo } from '@/db/repo'
import { MockRouter, type HttpMethod, type Route } from '@/services/api/router'
import { administrativeRoutes } from '@/services/api/routes/admin'
import {
  mediaRoutes, storeLinkLocally, storeMediaLocally, type UploadInput,
} from '@/services/api/routes/media'
import { publicRoutes } from '@/services/api/routes/public'

/** The in-browser "Express app". Admin routes first, then the public surface. */
export const router = new MockRouter()
  .register(administrativeRoutes)
  .register(mediaRoutes)
  .register(publicRoutes)

export interface RequestOptions {
  headers?: Record<string, string>
  /** Skip the offline sync queue (used by read-repair and background flush). */
  skipQueue?: boolean
  signal?: AbortSignal
}

/**
 * Set by the auth store at boot. Kept as a hook rather than an import so the
 * API client does not depend on the store (which depends on the client).
 */
let onUnauthenticated: (() => void) | null = null

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler
}

function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false
}

/** Entity/id extracted from a path so a queued mutation is traceable in the UI. */
function describeMutation(path: string): { entity: string; entityId: string } {
  const [, entity = 'unknown', id = ''] = path.split('/')
  return { entity, entityId: id }
}

async function enqueue(method: HttpMethod, path: string, payload: unknown): Promise<void> {
  if (method === 'GET') return
  const { entity, entityId } = describeMutation(path)
  const now = nowISO()
  const item: SyncQueueItem = {
    id: uid('sync'),
    method,
    endpoint: path,
    payload,
    entity,
    entityId,
    attempts: 0,
    status: 'queued',
    createdAt: now,
    updatedAt: now,
  }
  await repo.put('syncQueue', item)
}

async function callRemote<T>(
  method: HttpMethod,
  path: string,
  body: unknown,
  options: RequestOptions,
): Promise<ApiResponse<T>> {
  const url = `${env.apiBaseUrl}${path}`
  let response: globalThis.Response
  try {
    response = await fetch(url, {
      method,
      // Explicit: the session cookie must ride along. The spec default is
      // `same-origin`, but older WebViews defaulted to `omit`, and a silently
      // dropped cookie is indistinguishable from being signed out.
      credentials: 'same-origin',
      // Marks the request as XHR-ish, which a cross-site form post cannot
      // forge — a cheap second line of defence behind SameSite=Lax.
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        // Custom header, not Authorization: CGI-mode PHP on shared hosting
        // often strips Authorization before the script ever sees it.
        ...(getAuthToken() ? { 'X-Auth-Token': getAuthToken() as string } : {}),
        ...options.headers,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: options.signal,
    })
  } catch (networkError) {
    throw new ApiError(
      0,
      'network_error',
      `Could not reach the API at ${url}. ${
        networkError instanceof Error ? networkError.message : 'The request failed.'
      }`,
    )
  }

  const raw = await response.text()
  let payload: unknown = null
  try {
    payload = raw ? JSON.parse(raw) : null
  } catch {
    payload = null
  }

  // The server's own error envelope carries the most useful message.
  if (!response.ok) {
    const envelope = payload as { error?: string; message?: string } | null

    if (response.status === 401) {
      // Tell the app the session has gone so it shows the sign-in screen,
      // rather than leaving a "signed in" shell where every action fails.
      onUnauthenticated?.()
    }

    throw new ApiError(
      response.status,
      envelope?.error ?? 'request_failed',
      envelope?.message ?? `${method} ${path} failed with ${response.status}`,
    )
  }

  // A 200 that is not JSON means Apache served the SPA shell instead of the
  // API — i.e. the backend is not deployed, or api/config.php is missing.
  if (payload === null || typeof payload !== 'object') {
    throw new ApiError(
      502,
      'invalid_response',
      `${url} returned ${response.headers.get('content-type') ?? 'an unknown type'} instead of JSON. ` +
        'The API is probably not deployed, or api/config.php has not been created on the server.',
    )
  }

  return payload as ApiResponse<T>
}

/**
 * Issues a request against the mock server (or a real backend when
 * `VITE_USE_MOCK_API=false`) and unwraps the `data` envelope.
 */
export async function request<T>(
  method: HttpMethod,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  if (!env.useMockApi) {
    if (!isOnline() && method !== 'GET' && !options.skipQueue) {
      await enqueue(method, path, body)
      throw new ApiError(503, 'offline_queued', 'You are offline — this change was queued')
    }
    const remote = await callRemote<T>(method, path, body, options)
    return remote.data
  }

  // Mock mode: a touch of latency in dev makes loading states real.
  if (env.isDev) await sleep(method === 'GET' ? 60 : 120)

  const response = await router.dispatch<T>(method, path, body, options.headers ?? {})

  // The write succeeded locally. If we are offline, record it so it can be
  // replayed upstream once a real backend is reachable again.
  if (method !== 'GET' && !options.skipQueue && !isOnline()) {
    await enqueue(method, path, body)
  }
  return response.data
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, body, options),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>('DELETE', path, undefined, options),
  /** Route table for the self-documenting Admin -> Public API tab. */
  routes: (visibility?: Route['visibility']) => router.list(visibility),
}

/* -------------------------------------------------------------------------- */
/* File uploads                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Uploads an audio file.
 *
 * Multipart cannot go through the JSON envelope, so this is the one call that
 * bypasses `request()`: against a real backend it posts FormData and reports
 * progress via XHR; in mock mode it writes the Blob straight to IndexedDB.
 */
export async function uploadMedia(
  input: UploadInput,
  onProgress?: (percent: number) => void,
): Promise<SongMedia> {
  if (env.useMockApi) {
    onProgress?.(100)
    return storeMediaLocally(input)
  }

  const form = new FormData()
  form.append('file', input.file)
  if (input.label) form.append('label', input.label)
  if (input.kind) form.append('kind', input.kind)
  if (input.arrangementId) form.append('arrangementId', input.arrangementId)
  if (input.durationSeconds != null) form.append('durationSeconds', String(Math.round(input.durationSeconds)))

  const url = `${env.apiBaseUrl}/songs/${encodeURIComponent(input.songId)}/media`

  return new Promise<SongMedia>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url)
    xhr.withCredentials = true
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest')
    const token = getAuthToken()
    if (token) xhr.setRequestHeader('X-Auth-Token', token)
    // fetch() cannot report upload progress; a 40 MB WAV needs a progress bar.
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100))
    }
    xhr.onload = () => {
      let payload: { data?: SongMedia; error?: string; message?: string } | null = null
      try {
        payload = JSON.parse(xhr.responseText)
      } catch {
        payload = null
      }
      if (xhr.status >= 200 && xhr.status < 300 && payload?.data) {
        resolve(payload.data)
        return
      }
      reject(
        new ApiError(
          xhr.status,
          payload?.error ?? 'upload_failed',
          payload?.message ??
            (xhr.status === 413
              ? 'The server rejected that file as too large.'
              : `Upload failed with ${xhr.status}.`),
        ),
      )
    }
    xhr.onerror = () => reject(new ApiError(0, 'network_error', 'The upload could not reach the server.'))
    xhr.onabort = () => reject(new ApiError(0, 'aborted', 'Upload cancelled.'))
    xhr.send(form)
  })
}

/**
 * Uploads a profile photo.
 *
 * Multipart again, so it bypasses the JSON envelope for the same reason
 * `uploadMedia` does — but plain fetch is enough here: the image has already
 * been resized to a couple of hundred kilobytes, so there is no progress worth
 * reporting.
 */
export async function uploadAvatar(
  personId: string,
  image: Blob,
  /*
   * 'own' is the person's own picture and anybody may change their own.
   * 'official' is the face on the Sunday team screen and the API refuses it to
   * anyone but an admin — the check that matters is there, not here.
   */
  variant: 'own' | 'official' = 'own',
): Promise<string> {
  const form = new FormData()
  form.append('file', new File([image], 'avatar.jpg', { type: 'image/jpeg' }))

  const path = variant === 'official' ? 'official-avatar' : 'avatar'
  const token = getAuthToken()
  const response = await fetch(`${env.apiBaseUrl}/people/${encodeURIComponent(personId)}/${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      ...(token ? { 'X-Auth-Token': token } : {}),
    },
    body: form,
  })

  let payload: { data?: { avatarUrl: string }; error?: string; message?: string } | null = null
  try {
    payload = JSON.parse(await response.text())
  } catch {
    payload = null
  }
  if (response.ok && payload?.data?.avatarUrl) return payload.data.avatarUrl

  throw new ApiError(
    response.status,
    payload?.error ?? 'upload_failed',
    payload?.message ??
      (response.status === 413
        ? 'That image was too large.'
        : `Upload failed with ${response.status}.`),
  )
}

/** Attaches a practice video by URL (YouTube and friends). */
export async function addMediaLink(input: {
  songId: string
  url: string
  label?: string
  arrangementId?: string | null
}): Promise<SongMedia> {
  if (env.useMockApi) return storeLinkLocally(input)
  return request<SongMedia>('POST', `/songs/${encodeURIComponent(input.songId)}/links`, {
    url: input.url,
    label: input.label,
    arrangementId: input.arrangementId,
  })
}

/* -------------------------------------------------------------------------- */
/* Offline sync queue                                                         */
/* -------------------------------------------------------------------------- */

export async function pendingSyncItems(): Promise<SyncQueueItem[]> {
  const items = await repo.all('syncQueue')
  return items
    .filter((i) => i.status === 'queued' || i.status === 'failed')
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
}

export interface FlushResult {
  synced: number
  failed: number
}

/**
 * Replays queued mutations.
 *
 * In mock mode the write already landed in IndexedDB, so flushing simply
 * settles the queue — the code path is identical to the real one, which is
 * what the Workbox background-sync integration exercises.
 */
export async function flushSyncQueue(): Promise<FlushResult> {
  if (!isOnline()) return { synced: 0, failed: 0 }
  const queued = await pendingSyncItems()
  let synced = 0
  let failed = 0

  for (const item of queued) {
    await repo.patch('syncQueue', item.id, { status: 'syncing', attempts: item.attempts + 1 })
    try {
      if (!env.useMockApi) {
        await callRemote(item.method, item.endpoint, item.payload, {})
      }
      await repo.patch('syncQueue', item.id, { status: 'synced', lastError: undefined })
      synced += 1
    } catch (error) {
      failed += 1
      await repo.patch('syncQueue', item.id, {
        status: 'failed',
        lastError: error instanceof Error ? error.message : String(error),
      })
    }
  }

  // Keep the queue tidy: drop anything that has settled.
  const settled = (await repo.all('syncQueue')).filter((i) => i.status === 'synced')
  await Promise.all(settled.map((i) => repo.remove('syncQueue', i.id)))

  return { synced, failed }
}

/** Human-readable message for any thrown error, used by every error boundary. */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error) return error.message
  return 'Something went wrong'
}
